import { ClientSelectedServiceLevels } from '@provision/service-level-utils';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { AddPreSoldMembershipFormValues } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import {
  convertPriceToCents,
  getIntervalFromLengthInMonths,
} from 'src/components/AddPreSoldMembershipModal/util';
import { DynamicForm } from 'src/components/Forms';
import { getDynamicFormDefaultValues } from 'src/components/Forms/DynamicForm/dynamic-form-default-values';
import { ModalContainer } from 'src/components/ModalContainer';
import { DATE_FORMAT } from 'src/constants/date';
import {
  ProjectStages,
  SubmitProposalWonPayload,
  useGroupRules,
  useProjectStages,
  UserGroupRules,
  useSubmitProposalWon,
} from 'src/graphql';
import { usePreSoldMembership } from 'src/graphql/entities/pre-sold-membership/pre-sold-membership';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { ProposalWonFormValues, useProposalWonForm } from './use-proposal-won-form';

export const ProposalWonModal = () => {
  const { closeModal } = useModalMethods(Modals.ProposalWon);
  const { isOpened, modalProps } = useModalState(Modals.ProposalWon);
  const { showModal: openPsmModal, closeModal: closePsmModal } = useModalMethods(
    Modals.AddPreSoldMembership,
  );
  const { showSuccessAlert } = useNotifications();
  const { groupRules } = useGroupRules();

  const canUpdateSLA = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);

  const { projectStages } = useProjectStages({
    filters: [
      {
        attribute: 'projectStageId',
        operator: '=',
        value: ProjectStages.ActiveProject.toString(),
      },
    ],
    limit: 1,
  });

  const { proposalWonFormFields } = useProposalWonForm();
  const formMethods = useForm<ProposalWonFormValues>({
    defaultValues: getDynamicFormDefaultValues<ProposalWonFormValues>(proposalWonFormFields),
    mode: 'onChange',
  });
  const { formState, handleSubmit } = formMethods;
  const { submitProposalWon } = useSubmitProposalWon();
  const { createPreSoldMembership } = usePreSoldMembership();

  const onSubmitPSM = useCallback(
    async (data: AddPreSoldMembershipFormValues) => {
      const { intervalCount, intervalUnit } = getIntervalFromLengthInMonths(
        data.lengthInMonths,
      );
      const psmPayload = {
        addressId: modalProps?.address?.addressId,
        partnerId: modalProps?.address?.partnerId,
        amount: convertPriceToCents(data.price),
        intervalUnit,
        intervalCount,
        partnerProductId: data.partnerProductId,
        isScheduled: false,
      };

      closePsmModal();
      const result = await createPreSoldMembership(psmPayload);
      if (!result?.errors) {
        showSuccessAlert('Pre-sold membership created successfully');
      }
    },
    [
      closePsmModal,
      createPreSoldMembership,
      modalProps?.address?.addressId,
      modalProps?.address?.partnerId,
      showSuccessAlert,
    ],
  );

  const onSubmit = useCallback(
    async (data: ProposalWonFormValues) => {
      const project = modalProps?.project;
      const address = modalProps?.address;

      if (
        (!project?.id && !project?.projectId) ||
        (!address?.id && !address?.addressId) ||
        !data.estimatedDay1Date?.isValid()
      ) {
        return;
      }

      const payload: SubmitProposalWonPayload = {
        projectId: project.id,
        addressId: address.id,
        partnerId: address.partnerId ?? project.partnerId,
        estimatedDay1: data.estimatedDay1Date?.format(DATE_FORMAT),
        warrantyLength: data.warrantyLength?.value || 0,
        // The Desired Membership field was removed from this modal (1V-5466), but
        // SubmitProposalWonInput.desiredMembership is non-null — keep sending the
        // former default until the backend makes it optional.
        desiredMembership: ClientSelectedServiceLevels.Undecided,
        tosAccepted: data.tosAccepted,
        orgNotes: data.orgNotes,
        membershipSalesNotes: data.membershipSalesNotes,
        sendEducationalEmails: data.sendEducationalEmails,
      };

      closeModal();
      const result = await submitProposalWon(
        payload,
        {
          ...project,
          projectStage: projectStages?.[0],
        },
        address,
      );

      if (!result.errors) {
        showSuccessAlert('Proposal Won submitted successfully');

        if (data.hasPreSoldMembership && canUpdateSLA) {
          openPsmModal({
            addressId: address.id,
            onSubmit: onSubmitPSM,
          });
        }
      }
    },
    [
      canUpdateSLA,
      closeModal,
      modalProps?.address,
      modalProps?.project,
      onSubmitPSM,
      openPsmModal,
      projectStages,
      showSuccessAlert,
      submitProposalWon,
    ],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Proposal Won"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'proposal-won-cancel' },
        {
          label: 'Mark as won',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid,
          testId: 'proposal-won-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="proposal-won-modal"
    >
      <DynamicForm fields={proposalWonFormFields} formMethods={formMethods} sx={{ gap: 2 }} />
    </ModalContainer>
  );
};
